
import Button from '../../../components/base/Button';

export default function SolutionSection() {
  const solutions = [
    {
      icon: 'ri-lightbulb-line',
      title: 'Aprendizaje Práctico',
      description: 'Formación diseñada para la aplicación inmediata. Cada concepto viene con ejercicios específicos que puedes integrar en tu día a día desde el primer momento.',
      features: ['Webinars interactivos', 'Masterclasses especializadas', 'Guías de implementación', 'Ejercicios personalizados']
    },
    {
      icon: 'ri-team-line',
      title: 'Comunidad de Apoyo',
      description: 'Una red de personas reales que comprenden tu proceso. Comparte experiencias, celebra logros y supera obstáculos junto a quienes viven situaciones similares.',
      features: ['Grupos de apoyo', 'Encuentros presenciales', 'Chat comunitario 24/7', 'Compañeros de proceso']
    },
    {
      icon: 'ri-trophy-line',
      title: 'Seguimiento Personalizado',
      description: 'Sistema de coaching adaptado a tu ritmo y necesidades. Métricas claras de progreso y apoyo individual cuando lo necesites para mantener el momentum.',
      features: ['Coaching 1:1 disponible', 'Indicadores de progreso', 'Planes personalizados', 'Feedback continuo']
    }
  ];

  const methodology = [
    { step: '01', title: 'Diagnóstico', description: 'Evaluamos tu situación actual y objetivos específicos' },
    { step: '02', title: 'Planificación', description: 'Diseñamos tu ruta personalizada de transformación' },
    { step: '03', title: 'Implementación', description: 'Aplicamos técnicas probadas con apoyo constante' },
    { step: '04', title: 'Integración', description: 'Consolidamos los cambios en tu estilo de vida' },
    { step: '05', title: 'Evolución', description: 'Crecimiento continuo con la comunidad NEO' }
  ];

  const handleStartFreeTrial = () => {
    window.open('https://www.skool.com/experiencia-neo-8010', '_blank');
  };

  return (
    <section id="experiencia" className="py-32 bg-gradient-to-b from-white to-gray-50">
      <div className="container mx-auto px-8">
        <div className="max-w-4xl mx-auto text-center mb-20">
          <h2 className="text-5xl md:text-6xl font-light text-gray-900 mb-8 tracking-tight">
            La solución
            <span className="flex justify-center">
              <img 
                src="https://static.readdy.ai/image/eeb6f71c46431ac1fde6cad29ff61ccd/2e429e2f25958de38e09615a49cf72b8.png" 
                alt="NEO Logo" 
                className="block h-20 w-auto mt-2"
              />
            </span>
          </h2>
          <p className="text-2xl text-gray-600 leading-relaxed font-light">
            Un enfoque humano que combina herramientas prácticas con el apoyo de una comunidad real. 
            Transformación auténtica, paso a paso.
          </p>
        </div>

        {/* Solutions */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-24">
          {solutions.map((solution, index) => (
            <div key={index} className="group">
              <div className="bg-white rounded-3xl p-10 shadow-sm border border-gray-100 h-full hover:shadow-xl transition-all duration-500 transform hover:-translate-y-2">
                <div className="w-20 h-20 bg-gradient-to-br from-[#6607a5]/10 to-purple-100 rounded-2xl flex items-center justify-center mb-8 group-hover:scale-110 transition-transform duration-300">
                  <i className={`${solution.icon} text-3xl text-[#6607a5]`}></i>
                </div>
                <h3 className="text-2xl font-medium text-gray-900 mb-6">{solution.title}</h3>
                <p className="text-gray-600 leading-relaxed font-light text-lg mb-8">{solution.description}</p>
                <div className="space-y-3">
                  {solution.features.map((feature, idx) => (
                    <div key={idx} className="flex items-center text-gray-700">
                      <div className="w-2 h-2 bg-gradient-to-r from-[#6607a5] to-purple-400 rounded-full mr-3"></div>
                      <span className="font-light">{feature}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Methodology */}
        <div className="max-w-6xl mx-auto">
          <h3 className="text-4xl font-light text-center text-gray-900 mb-16">Nuestra metodología de 5 fases</h3>
          <div className="relative">
            {/* Connection line */}
            <div className="absolute top-12 left-0 right-0 h-0.5 bg-gradient-to-r from-[#6607a5] to-purple-400 hidden lg:block"></div>
            
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
              {methodology.map((phase, index) => (
                <div key={index} className="text-center relative">
                  <div className="w-24 h-24 bg-gradient-to-br from-[#6607a5] to-purple-600 rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg relative z-10">
                    <span className="text-white font-light text-xl">{phase.step}</span>
                  </div>
                  <h4 className="text-xl font-medium text-gray-900 mb-4">{phase.title}</h4>
                  <p className="text-gray-600 font-light leading-relaxed">{phase.description}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Premium CTA */}
        <div className="mt-24">
          <div className="bg-gradient-to-r from-gray-900 via-[#6607a5] to-gray-900 rounded-3xl p-12 text-white max-w-5xl mx-auto relative overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent animate-pulse"></div>
            <div className="relative z-10 text-center">
              <h4 className="text-4xl font-light mb-6">Para personas como tú</h4>
              <p className="text-xl text-gray-200 mb-8 font-light leading-relaxed max-w-3xl mx-auto">
                Diseñada para quienes sienten que necesitan un cambio pero no saben por dónde empezar. 
                Un espacio seguro para tu crecimiento personal.
              </p>
              
              <div className="flex justify-center">
                <Button 
                  size="lg" 
                  className="bg-gradient-to-r from-[#6607a5] to-purple-700 hover:from-purple-700 hover:to-[#6607a5] text-white font-medium border-[#6607a5] hover:border-purple-700 shadow-xl hover:shadow-2xl px-6 sm:px-8 md:px-10 py-3 sm:py-4 text-sm sm:text-base md:text-lg"
                  onClick={() => {
                    const programSection = document.getElementById('programa');
                    programSection?.scrollIntoView({ behavior: 'smooth' });
                  }}
                >
                  <i className="ri-heart-line"></i>
                  <span className="hidden sm:inline">Comenzar mi proceso</span>
                  <span className="sm:hidden flex flex-col leading-tight">
                    <span>Comenzar</span>
                    <span>mi proceso</span>
                  </span>
                </Button>
              </div>
              
            </div>
          </div>
        </div>

        {/* Benefits and Guarantee Section */}
        <div className="container mx-auto px-4 md:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-12 max-w-7xl mx-auto">
            {/* Benefits Card */}
            <div className="bg-white rounded-2xl lg:rounded-3xl p-6 lg:p-10 shadow-xl border border-gray-200">
              <h3 className="text-2xl md:text-3xl lg:text-4xl font-light text-[#6607a5] mb-6 lg:mb-8 text-center">LO QUE GANARÁS AL UNIRTE:</h3>
              <div className="space-y-4 lg:space-y-6">
                <div className="flex items-start">
                  <div className="w-10 h-10 md:w-12 md:h-12 lg:w-16 lg:h-16 bg-gradient-to-br from-[#6607a5]/10 to-purple-100 rounded-full flex items-center justify-center mr-3 lg:mr-4 flex-shrink-0">
                    <i className="ri-compass-3-line text-lg md:text-xl lg:text-2xl text-[#6607a5]"></i>
                  </div>
                  <div>
                    <h4 className="text-base md:text-lg lg:text-xl font-medium text-gray-900 mb-1 lg:mb-2">Descubre Tu Propósito de Vida</h4>
                    <p className="text-sm md:text-base text-gray-600 leading-relaxed">Crea la mentalidad correcta para lograr tus metas, incluso si partes desde cero perdido/a</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="w-10 h-10 md:w-12 md:h-12 lg:w-16 lg:h-16 bg-gradient-to-br from-[#6607a5]/10 to-purple-100 rounded-full flex items-center justify-center mr-3 lg:mr-4 flex-shrink-0">
                    <i className="ri-stop-circle-line text-lg md:text-xl lg:text-2xl text-[#6607a5]"></i>
                  </div>
                  <div>
                    <h4 className="text-base md:text-lg lg:text-xl font-medium text-gray-900 mb-1 lg:mb-2">Termina con el Autosabotaje</h4>
                    <p className="text-sm md:text-base text-gray-600 leading-relaxed">Elimina tus bloqueos mentales y desarrolla una confianza inquebrantable.</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="w-10 h-10 md:w-12 md:h-12 lg:w-16 lg:h-16 bg-gradient-to-br from-[#6607a5]/10 to-purple-100 rounded-full flex items-center justify-center mr-3 lg:mr-4 flex-shrink-0">
                    <i className="ri-group-line text-lg md:text-xl lg:text-2xl text-[#6607a5]"></i>
                  </div>
                  <div>
                    <h4 className="text-base md:text-lg lg:text-xl font-medium text-gray-900 mb-1 lg:mb-2">Únete a una Comunidad Potenciadora</h4>
                    <p className="text-sm md:text-base text-gray-600 leading-relaxed">Deja atrás los entornos que te limitan y únete a una comunidad que te impulse.</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="w-10 h-10 md:w-12 md:h-12 lg:w-16 lg:h-16 bg-gradient-to-br from-[#6607a5]/10 to-purple-100 rounded-full flex items-center justify-center mr-3 lg:mr-4 flex-shrink-0">
                    <i className="ri-heart-pulse-line text-lg md:text-xl lg:text-2xl text-[#6607a5]"></i>
                  </div>
                  <div>
                    <h4 className="text-base md:text-lg lg:text-xl font-medium text-gray-900 mb-1 lg:mb-2">Crea Hábitos Sanos</h4>
                    <p className="text-sm md:text-base text-gray-600 leading-relaxed">Supera la procrastinación y construye hábitos que desaten todo tu potencial.</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="w-10 h-10 md:w-12 md:h-12 lg:w-16 lg:h-16 bg-gradient-to-br from-[#6607a5]/10 to-purple-100 rounded-full flex items-center justify-center mr-3 lg:mr-4 flex-shrink-0">
                    <i className="ri-emotion-line text-lg md:text-xl lg:text-2xl text-[#6607a5]"></i>
                  </div>
                  <div>
                    <h4 className="text-base md:text-lg lg:text-xl font-medium text-gray-900 mb-1 lg:mb-2">Gestiona Tus Emociones</h4>
                    <p className="text-sm md:text-base text-gray-600 leading-relaxed">Aprende a regular el estrés, la ansiedad y las emociones negativas de forma natural.</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="w-10 h-10 md:w-12 md:h-12 lg:w-16 lg:h-16 bg-gradient-to-br from-[#6607a5]/10 to-purple-100 rounded-full flex items-center justify-center mr-3 lg:mr-4 flex-shrink-0">
                    <i className="ri-time-line text-lg md:text-xl lg:text-2xl text-[#6607a5]"></i>
                  </div>
                  <div>
                    <h4 className="text-base md:text-lg lg:text-xl font-medium text-gray-900 mb-1 lg:mb-2">Optimiza Tu Tiempo</h4>
                    <p className="text-sm md:text-base text-gray-600 leading-relaxed">Organiza tu día de manera eficiente y encuentra el equilibrio entre trabajo y vida personal.</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="w-10 h-10 md:w-12 md:h-12 lg:w-16 lg:h-16 bg-gradient-to-br from-[#6607a5]/10 to-purple-100 rounded-full flex items-center justify-center mr-3 lg:mr-4 flex-shrink-0">
                    <i className="ri-star-line text-lg md:text-xl lg:text-2xl text-[#6607a5]"></i>
                  </div>
                  <div>
                    <h4 className="text-base md:text-lg lg:text-xl font-medium text-gray-900 mb-1 lg:mb-2">Alcanza Tu Máximo Potencial</h4>
                    <p className="text-sm md:text-base text-gray-600 leading-relaxed">Desarrolla todas tus capacidades y conviértete en la mejor versión de ti mismo.</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Guarantee Card */}
            <div className="bg-white rounded-2xl lg:rounded-3xl p-6 lg:p-10 shadow-xl border border-gray-200 text-center">
              <div className="mb-6 lg:mb-8">
                <div className="w-16 h-16 md:w-20 md:h-20 lg:w-24 lg:h-24 bg-gradient-to-br from-yellow-400 to-orange-500 rounded-full flex items-center justify-center mx-auto mb-4 lg:mb-6 shadow-lg">
                  <span className="text-white font-bold text-xs md:text-sm">100%</span>
                </div>
                <h3 className="text-2xl md:text-3xl lg:text-4xl font-light text-[#6607a5] mb-3 lg:mb-4">GARANTÍA DE PROGRESO</h3>
              </div>
              
              <div className="mb-6 lg:mb-8">
                <h4 className="text-lg md:text-xl lg:text-2xl font-medium text-[#6607a5] mb-4 lg:mb-6">¿Qué pasa si no veo resultados?</h4>
                <p className="text-sm md:text-base text-gray-600 leading-relaxed mb-4 lg:mb-6">Si en tu primer mes no percibes avances en al menos alguna de las señales</p>
                <div className="bg-gradient-to-r from-[#6607a5]/10 to-purple-100 rounded-xl lg:rounded-2xl p-4 lg:p-6 mb-4 lg:mb-6 border border-[#6607a5]/20">
                  <p className="text-[#6607a5] font-medium text-sm md:text-base lg:text-lg">te regalamos una Sesión 1:1 de Diagnóstico + Plan de seguimiento personalizado.</p>
                </div>
                <p className="text-sm md:text-base text-gray-600 leading-relaxed">Te acompañamos hasta que lo consigas porque de verdad nos importa.</p>
              </div>
              
              <div className="bg-gradient-to-r from-blue-400/20 to-purple-400/20 rounded-2xl lg:rounded-3xl p-6 lg:p-8 border border-blue-400/30">
                <div className="w-14 h-14 md:w-16 md:h-16 lg:w-20 lg:h-20 bg-gradient-to-br from-yellow-400 to-orange-500 rounded-full flex items-center justify-center mx-auto mb-3 lg:mb-4 shadow-lg">
                  <span className="text-white font-bold text-base md:text-lg">7</span>
                </div>
                <h4 className="text-xl md:text-2xl lg:text-3xl font-bold text-gray-900 mb-1 lg:mb-2">7 DÍAS DE</h4>
                <h4 className="text-xl md:text-2xl lg:text-3xl font-bold text-gray-900 mb-4 lg:mb-6">PRUEBA GRATIS</h4>
                <button 
                  onClick={handleStartFreeTrial}
                  className="w-full bg-gradient-to-r from-[#6607a5] to-purple-700 hover:from-purple-700 hover:to-[#6607a5] text-white font-bold py-3 lg:py-4 px-3 sm:px-4 md:px-6 lg:px-8 rounded-full shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:scale-105 whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-gift-line text-base sm:text-lg lg:text-xl mr-1 sm:mr-2"></i>
                  <span className="text-xs sm:text-sm md:text-base">COMENZAR PRUEBA GRATIS</span>
                </button>
              </div>
            </div>
          </div>
        </div>

      </div>
    </section>
  );
}
